import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { GenericUsageEventMetricsApi } from '../../services/api/GenericUsageEventMetricsApi';
import { UsageEventsApi } from '../../services/ApiService';
import { 
  MetricCard, 
  LoadingChart, 
  EmptyState, 
  DateRangePicker, 
  ExportButton,
  ActivityHeatmap,
  ProgressBar
} from '../../components/analytics/SharedAnalyticsComponents';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

/**
 * UserUsageEventsDashboard - Individual User Activity History
 * Timeline visualization, calendar heatmap, feature usage radar, engagement scoring
 */
export default function UserUsageEventsDashboard() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(Date.now() - 30 * 86400000); // 30 days
    return { from: startDate.toISOString(), to: endDate.toISOString() };
  });

  const [data, setData] = useState({
    timeline: [],
    events: [],
    summary: null,
    featureUsage: [],
    engagementScore: [],
    heatmapData: [],
    cohortComparison: null
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventFilter, setEventFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);

  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');
  const isSelf = user?.userId?.toString() === userId;

  const loadData = useCallback(async () => {
    if (!userId || (!isAdmin && !isSelf)) return;
    
    setLoading(true);
    setError(null);

    try {
      const startDate = new Date(dateRange.from);
      const endDate = new Date(dateRange.to);

      const [
        timelineRes,
        eventsRes
      ] = await Promise.all([
        GenericUsageEventMetricsApi.getUserActivityTimeline(userId, startDate, endDate)
          .then(r => r.ok ? r.json() : []).catch(() => []),
        UsageEventsApi.userSearch({ 
          userId, 
          from: dateRange.from, 
          to: dateRange.to,
          eventType: eventFilter || undefined,
          page,
          size: pageSize
        }).then(r => r.ok ? r.json() : { content: [], totalElements: 0 }).catch(() => ({ content: [], totalElements: 0 }))
      ]);

      // Process timeline data
      const timeline = timelineRes || [];
      const events = eventsRes.content || [];
      
      // Calculate feature usage distribution
      const featureUsage = {};
      events.forEach(event => {
        const feature = event.category || event.eventType;
        if (!featureUsage[feature]) {
          featureUsage[feature] = { feature, count: 0 };
        }
        featureUsage[feature].count++;
      });

      const featureUsageArray = Object.values(featureUsage)
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

      // Generate heatmap data
      const heatmapData = generateHeatmapFromEvents(events);

      // Calculate engagement score over time
      const engagementScore = calculateEngagementScore(timeline);

      // Mock cohort comparison (would come from backend)
      const cohortComparison = {
        userAverage: events.length / 30,
        cohortAverage: (events.length / 30) * 1.2,
        percentile: 65
      };

      setData({
        timeline,
        events,
        summary: {
          totalEvents: events.length,
          totalElements: eventsRes.totalElements,
          uniqueEventTypes: new Set(events.map(e => e.eventType)).size,
          dateRange: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
        },
        featureUsage: featureUsageArray,
        engagementScore,
        heatmapData,
        cohortComparison
      });
    } catch (err) {
      setError(err);
      console.error('Failed to load user usage data:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, dateRange, eventFilter, page, pageSize, isAdmin, isSelf]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateHeatmapFromEvents = (events) => {
    const heatmap = [];
    events.forEach(event => {
      const date = new Date(event.timestamp);
      const day = date.getDay();
      const hour = date.getHours();
      const existing = heatmap.find(h => h.day === day && h.hour === hour);
      if (existing) {
        existing.value++;
      } else {
        heatmap.push({ day, hour, value: 1 });
      }
    });
    return heatmap;
  };

  const calculateEngagementScore = (timeline) => {
    // Simple engagement scoring based on activity frequency
    const scores = [];
    const dailyBuckets = {};
    
    timeline.forEach(item => {
      const date = new Date(item.timestamp || item.date);
      const dateKey = date.toISOString().split('T')[0];
      if (!dailyBuckets[dateKey]) {
        dailyBuckets[dateKey] = 0;
      }
      dailyBuckets[dateKey] += item.count || 1;
    });

    Object.entries(dailyBuckets).forEach(([date, count]) => {
      // Score: 0-100 based on activity (capped at 50 events = 100 score)
      const score = Math.min((count / 50) * 100, 100);
      scores.push({ date, score });
    });

    return scores.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const formatEventCard = (event, index) => {
    const timestamp = new Date(event.timestamp);
    const isRecent = Date.now() - timestamp < 3600000; // Last hour

    return (
      <div 
        key={event.id || index}
        style={{
          background: 'var(--card-bg)',
          border: isRecent ? '2px solid #28a745' : '1px solid var(--border-light)',
          borderLeft: `4px solid ${COLORS[index % COLORS.length]}`,
          borderRadius: '8px',
          padding: '0.75rem',
          marginBottom: '0.75rem',
          fontSize: '.75rem',
          position: 'relative'
        }}
      >
        {isRecent && (
          <div style={{ 
            position: 'absolute', 
            top: '0.5rem', 
            right: '0.5rem', 
            background: '#28a745', 
            color: '#fff', 
            padding: '0.125rem 0.5rem', 
            borderRadius: '12px', 
            fontSize: '.65rem',
            fontWeight: 600
          }}>
            RECENT
          </div>
        )}
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <div style={{ fontWeight: 600, color: '#007bff' }}>
            {event.eventType}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '.7rem' }}>
            {timestamp.toLocaleString()}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem', fontSize: '.7rem', color: 'var(--text-secondary)' }}>
          {event.category && (
            <>
              <strong>Category:</strong>
              <span>{event.category}</span>
            </>
          )}
          {event.action && (
            <>
              <strong>Action:</strong>
              <span>{event.action}</span>
            </>
          )}
          {event.entityType && (
            <>
              <strong>Entity:</strong>
              <span>{event.entityType} {event.entityId && `#${event.entityId}`}</span>
            </>
          )}
          {event.sessionId && (
            <>
              <strong>Session:</strong>
              <span style={{ fontFamily: 'monospace', fontSize: '.65rem' }}>{event.sessionId.slice(0, 12)}...</span>
            </>
          )}
        </div>

        {event.metadata && Object.keys(event.metadata).length > 0 && (
          <details style={{ marginTop: '0.5rem', fontSize: '.65rem' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>Metadata</summary>
            <pre style={{ 
              background: '#f5f5f5', 
              padding: '0.5rem', 
              borderRadius: '4px', 
              marginTop: '0.25rem',
              overflow: 'auto',
              maxHeight: '100px'
            }}>
              {JSON.stringify(event.metadata, null, 2)}
            </pre>
          </details>
        )}
      </div>
    );
  };

  if (!userId) {
    return (
      <div className="settings-container">
        <h2>User Usage History</h2>
        <EmptyState message="No user ID provided" icon="❓" />
      </div>
    );
  }

  if (!isAdmin && !isSelf) {
    return (
      <div className="settings-container">
        <h2>User Usage History</h2>
        <EmptyState message="You can only view your own usage history" icon="🔒" />
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button onClick={() => navigate('/settings/usage-events')} className="secondary" style={{ padding: '0.25rem 0.5rem' }}>
              ← Back
            </button>
            <h2 style={{ margin: 0 }}>👤 User Activity: #{userId}</h2>
          </div>
          <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', margin: '0.25rem 0 0 2.5rem' }}>
            {isSelf ? 'Your activity history' : 'Detailed user event timeline'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <DateRangePicker 
            from={dateRange.from} 
            to={dateRange.to} 
            onChange={setDateRange}
          />
          <button onClick={loadData} disabled={loading}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c' }}>
          Error loading data: {error.message}
        </div>
      )}

      {/* KPI Cards */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[1, 2, 3, 4].map(i => <MetricCard key={i} loading />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <MetricCard
            label="Total Events"
            value={data.summary?.totalElements?.toLocaleString() || '0'}
            subtitle={`${data.summary?.dateRange || 0} days tracked`}
            color="#007bff"
          />
          <MetricCard
            label="Events (Page)"
            value={data.summary?.totalEvents?.toLocaleString() || '0'}
            subtitle={`Showing ${page * pageSize + 1}-${Math.min((page + 1) * pageSize, data.summary?.totalElements || 0)}`}
            color="#28a745"
          />
          <MetricCard
            label="Event Types Used"
            value={data.summary?.uniqueEventTypes?.toLocaleString() || '0'}
            subtitle="Distinct interactions"
            color="#ffc107"
          />
          <MetricCard
            label="Engagement Percentile"
            value={`${data.cohortComparison?.percentile || 0}%`}
            subtitle="vs cohort average"
            color="#17a2b8"
          />
        </div>
      )}

      {/* Engagement Score & Cohort Comparison */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Engagement Score Timeline */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Engagement Score Over Time</h3>
          {loading ? (
            <LoadingChart height={250} />
          ) : data.engagementScore.length === 0 ? (
            <EmptyState message="No engagement data" />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.engagementScore}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(v) => new Date(v).toLocaleDateString()}
                  style={{ fontSize: '.7rem' }}
                />
                <YAxis domain={[0, 100]} style={{ fontSize: '.7rem' }} />
                <Tooltip 
                  labelFormatter={(v) => new Date(v).toLocaleDateString()}
                  contentStyle={{ fontSize: '.75rem' }}
                />
                <Line type="monotone" dataKey="score" stroke="#007bff" strokeWidth={2} name="Engagement Score" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Cohort Comparison */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>User vs Cohort</h3>
          {loading ? (
            <LoadingChart height={250} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
              <div>
                <div style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  Daily Events (User)
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#007bff' }}>
                  {data.cohortComparison?.userAverage?.toFixed(1) || '0'}
                </div>
              </div>
              
              <div>
                <div style={{ fontSize: '.7rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>
                  Daily Events (Cohort Avg)
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#28a745' }}>
                  {data.cohortComparison?.cohortAverage?.toFixed(1) || '0'}
                </div>
              </div>

              <ProgressBar
                value={data.cohortComparison?.percentile || 0}
                max={100}
                label="Percentile Ranking"
                color="#17a2b8"
              />

              <div style={{ fontSize: '.7rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                {data.cohortComparison?.percentile >= 75 ? (
                  <div style={{ color: '#28a745' }}>🎉 High engagement user!</div>
                ) : data.cohortComparison?.percentile >= 50 ? (
                  <div style={{ color: '#ffc107' }}>👍 Above average engagement</div>
                ) : (
                  <div style={{ color: '#dc3545' }}>📉 Below average engagement</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Heatmap & Feature Usage Radar */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Calendar Heatmap */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Activity Heatmap (Weekly Pattern)</h3>
          {loading ? (
            <LoadingChart height={200} />
          ) : (
            <ActivityHeatmap data={data.heatmapData} max={20} />
          )}
        </div>

        {/* Feature Usage Radar */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Feature Usage Distribution</h3>
          {loading ? (
            <LoadingChart height={200} />
          ) : data.featureUsage.length === 0 ? (
            <EmptyState message="No feature data" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <RadarChart data={data.featureUsage}>
                <PolarGrid />
                <PolarAngleAxis dataKey="feature" style={{ fontSize: '.65rem' }} />
                <PolarRadiusAxis style={{ fontSize: '.65rem' }} />
                <Radar name="Usage" dataKey="count" stroke="#007bff" fill="#007bff" fillOpacity={0.6} />
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Event Timeline */}
      <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '.95rem' }}>📅 Event Timeline</h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Filter by event type..."
              value={eventFilter}
              onChange={(e) => {
                setEventFilter(e.target.value);
                setPage(0);
              }}
              style={{ fontSize: '.75rem', padding: '0.25rem 0.5rem' }}
            />
            <ExportButton data={data.events} filename={`user-${userId}-events`} />
          </div>
        </div>

        {loading ? (
          <LoadingChart height={400} />
        ) : data.events.length === 0 ? (
          <EmptyState message="No events found for this time period" icon="📭" />
        ) : (
          <>
            <div style={{ 
              maxHeight: '600px', 
              overflowY: 'auto',
              borderTop: '2px solid var(--border-light)',
              paddingTop: '1rem'
            }}>
              {data.events.map((event, index) => formatEventCard(event, index))}
            </div>

            {/* Pagination */}
            {data.summary && data.summary.totalElements > pageSize && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid var(--border-light)',
                fontSize: '.75rem'
              }}>
                <div style={{ color: 'var(--text-muted)' }}>
                  Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, data.summary.totalElements)} of {data.summary.totalElements}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="secondary"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    style={{ padding: '0.25rem 0.75rem' }}
                  >
                    ← Previous
                  </button>
                  <button
                    className="secondary"
                    onClick={() => setPage(page + 1)}
                    disabled={(page + 1) * pageSize >= data.summary.totalElements}
                    style={{ padding: '0.25rem 0.75rem' }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
